import { type IncludedFinalGrade } from "@clipboard-health/contract-backend-main";

import { useGetContractorBadgeVerification } from "../api/useGetContractorBadgeVerification";
import { extractGradeFromVerificationData } from "../utils/extractGradeFromVerificationData";

interface UseGradingFlowParams {
  shiftId: string;
  enabled: boolean;
}

interface UseGradingFlowResult {
  gradeResult: IncludedFinalGrade | undefined;
  /** True once the grade query has completed its first fetch (with or without a grade). */
  isQuerySettled: boolean;
}

/**
 * Fetches the badge verification grade after a successful submission.
 *
 * The query key includes `includeFinalGrade`, so this is a separate cache
 * entry that won't conflict with non-grade fetches. Timeout handling lives
 * in the dialog component (a hard timer on the GRADING step).
 */
export function useGradingFlow(params: UseGradingFlowParams): UseGradingFlowResult {
  const { shiftId, enabled } = params;

  const { data: gradeQueryData, isFetched } = useGetContractorBadgeVerification(shiftId, {
    enabled,
    includeFinalGrade: true,
  });
  const gradeResult = enabled ? extractGradeFromVerificationData(gradeQueryData) : undefined;
  const isQuerySettled = enabled && isFetched;

  return { gradeResult, isQuerySettled };
}
